import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Space,
  Typography,
  Select,
  Button,
  Divider,
  Tooltip,
  Badge,
  Dropdown,
  Menu
} from 'antd';
import {
  CalculatorOutlined,
  SaveOutlined,
  HistoryOutlined,
  ClearOutlined,
  DownOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { formatTime, parseTime } from '@/lib';
import DraftManager from './DraftManager';
import CreatableSelect from 'react-select/creatable';
import InspectionBtn from '@/components/InspectionBtn';

const { Title } = Typography;
const { Option } = Select;

const InspectionHeader = ({
  control,
  eventlist,
  terminology,
  setTerminology,
  handleFormChange,
  handleSaveDraft,
  isLoading,
  isDraftAvailable,
  lastSaved,
  onDraftSelected,
  currentFormData,
  draftsList = [],
  reportName = "Inspection"
}) => {
  const [showDraftManager, setShowDraftManager] = useState(false);

  const handleQuickLoadDraft = (draftKey) => {
    onDraftSelected(draftKey);
  };

  const recentDrafts = draftsList.slice(0, 5);
   const draftDropdownMenu = (
    <Menu>
      {recentDrafts.length > 0 ? (
        <>
          {recentDrafts.map(draft => (
            <Menu.Item
              key={draft.draftKey}
              onClick={() => handleQuickLoadDraft(draft.draftKey)}
            >
               <div>
                <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                  {draft.title}
                </div>
                <div style={{ fontSize: '10px', color: '#666' }}>
                  {new Date(draft.savedAt).toLocaleDateString()}
                </div>
              </div>
            </Menu.Item>
          ))}
          <Menu.Divider />
          <Menu.Item onClick={() => setShowDraftManager(true)}>
            <span style={{ color: '#1890ff' }}>View All Drafts...</span>
          </Menu.Item>
        </>
      ) : (
        <Menu.Item disabled>
          No drafts available
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <>
      {/* Desktop View - Original Layout */}
      <Card size="small" style={{ marginBottom: "5px" }} className=" hidden lg:block">
        <div className="space-y-4 ">
          <div className="flex flex-col lg:flex-row lg:justify-between gap-4  ">
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 flex-1 ">
              <Title level={5} className="mb-0 font-bold">
                {reportName} Report
              </Title>
              <Controller
                name="eventType"
                control={control}
                render={({ field }) => (
                  <CreatableSelect
                    {...field}
                    className="sm:w-auto sm:min-w-[200px]"
                    placeholder="Select Event"
                    onChange={(value) => {
                      field.onChange(value);
                      const getTerminology = eventlist?.find(x => x?.event_type === value);
                      setTerminology(getTerminology?.naming || "");
                      handleFormChange("eventType", value);
                    }}
                    options={eventlist?.map(x => ({
                      value: x.event_type,
                      label: x.event_type,
                    }))}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "32px",
                        height: "32px",
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        padding: "4px",
                      }),
                      clearIndicator: (base) => ({
                        ...base,
                        padding: "4px",
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: "0 6px",
                      }),
                      input: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                      }),
                      option: (base) => ({
                        ...base,
                        padding: "4px 8px",
                        minHeight: "28px",
                      }),
                    }}
                  />
                )}
              />

              <Controller
                name="dateTime"
                control={control}
                render={({ field: { onChange, value, ...rest } }) => (
                  <input
                    type="datetime-local"
                    className="w-full sm:w-auto rounded-md border px-2 py-1 text-sm shadow-sm focus:ring-2 focus:ring-green-500"
                    value={value ? dayjs(value).format("YYYY-MM-DDTHH:mm") : ""}
                    onChange={(e) => {
                      const dateTimeValue = e.target.value
                      onChange(dateTimeValue);
                      handleFormChange("dateTime", dateTimeValue);
                    }}
                    {...rest}
                  />
                )}
              />



              <InspectionBtn
                variant="primary"
                size="xs"
                loading={isLoading}
                onClick={handleSaveDraft}
                icon={<SaveOutlined />}
              >
                Save Draft
              </InspectionBtn>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Dropdown
                overlay={draftDropdownMenu}
                trigger={['click']}
                disabled={!isDraftAvailable}
              >
                <Button
                  icon={<HistoryOutlined />}
                  loading={isLoading}
                  size="small"
                  className="w-full sm:w-auto"
                >
                  Load Draft
                  {draftsList.length > 0 && (
                    <Badge
                      count={draftsList.length}
                      size="small"
                      style={{ marginLeft: '4px' }}
                    />
                  )}
                  <DownOutlined style={{ fontSize: '10px', marginLeft: '4px' }} />
                </Button>
              </Dropdown>

            </div>
          </div>
        </div>
      </Card>

      {/* Mobile Sticky Header - Higher z-index (60) */}
      <div className="lg:hidden fixed top-0 mx-2 left-0 right-0 z-[60] bg-white  border-gray-200">
        <div className="px-2 py-1">
          <div className="flex justify-between items-center">
            {/* Left section */}
            <div className="flex gap-1 items-center">
              <Controller
                name="eventType"
                control={control}
                render={({ field }) => (
                  <CreatableSelect
                    {...field}
                    placeholder="Events"
                    className="w-28 text-xs"
                    onChange={(value) => {
                      field.onChange(value);
                      const getTerminology = eventlist?.find(x => x?.event_type === value);
                      setTerminology(getTerminology?.naming || "");
                      handleFormChange("eventType", value);
                    }}
                    options={eventlist?.map(x => ({
                      value: x.event_type,
                      label: x.event_type,
                    }))}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: "20px",
                        height: "20px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        borderColor: state.isFocused ? "#1677ff" : base.borderColor,
                        boxShadow: state.isFocused ? "0 0 0 1px #1677ff" : base.boxShadow,
                        "&:hover": { borderColor: "#1677ff" },
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: "0 4px",
                        height: "20px",
                      }),
                      input: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                        fontSize: "12px",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontSize: "12px",
                        lineHeight: "20px",
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        height: "20px",
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        padding: "0 2px",
                      }),
                      clearIndicator: (base) => ({
                        ...base,
                        padding: "0 2px",
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: "11px",
                        minHeight: "20px",
                        padding: "2px 6px",
                        backgroundColor: state.isFocused ? "#f0f7ff" : "white",
                        color: "#333",
                      }),
                      menu: (base) => ({
                        ...base,
                        fontSize: "11px",
                        zIndex: 9999,
                      }),
                    }}

                  />
                )}
              />

              <Controller
                name="dateTime"
                control={control}
                render={({ field: { onChange, value, ...rest } }) => (
                  <input
                    type="datetime-local"
                    className="w-35 rounded-sm border border-gray-300 px-2  text-xs focus:ring-1 focus:ring-green-500"
                    value={value ? dayjs(value).format("YYYY-MM-DDTHH:mm") : ""}
                    onChange={(e) => {
                      const dateTimeValue = e.target.value
                      onChange(dateTimeValue);
                       handleFormChange("dateTime", dateTimeValue);
                    }}
                    {...rest}
                  />
                )}
              />

              <InspectionBtn
                icon={<SaveOutlined />}
                onClick={handleSaveDraft}
                loading={isLoading}
                type="primary"
                size="small"
                style={{
                  height: "20px",
                  fontSize: "11px",
                }}
              >
                Save
              </InspectionBtn>
            </div>

            {/* Right section */}
            <div className="flex gap-1 items-center">
              <Dropdown overlay={draftDropdownMenu} trigger={['click']} disabled={!isDraftAvailable}>
                <Button
                  icon={<HistoryOutlined />}
                  loading={isLoading}
                  size="small"
                  style={{
                    height: "20px",
                    fontSize: "11px",
                    lineHeight: "20px",
                  }}
                >
                  Load
                  {draftsList.length > 0 && (
                    <Badge count={draftsList.length} size="small" offset={[4, -2]} />
                  )}
                </Button>
              </Dropdown>


            </div>
          </div>
        </div>
      </div>

       <div className="lg:hidden h-7  "></div>
       <DraftManager
        visible={showDraftManager}
        onClose={() => setShowDraftManager(false)}
        onDraftSelected={onDraftSelected}
        currentFormData={currentFormData}
        reportType={reportName === "Inspection" ? "INSPECTION_REPORT" : reportName}
      />
    </>
  );
};
export default InspectionHeader